import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import KkpBriefAttachmentsViewer from './KkpBriefAttachmentsViewer';
import { AppAlert, AppButton, AppModal } from '../ui';
import kkpService from '../../services/kkpService';
import { BRIEF_ACCEPTED_FILE_TYPES, formatBriefFileSize } from '../../utils/kkpBriefFichierUtils';
import { canSkipBatValideImage, venteCanalLabel, ventePeriodeLabel } from '../../utils/kkpBatValide';
import '../../styles/kkp-brief-fichiers.scss';
import '../../styles/kkp-pub-graphiste.scss';

function fileIconFromName(name = '') {
  const lower = name.toLowerCase();
  if (/\.(jpe?g|png|gif|webp|svg)$/.test(lower)) return 'mdi:file-image-outline';
  if (/\.pdf$/.test(lower)) return 'mdi:file-pdf-box';
  if (/\.(doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/.test(lower)) return 'mdi:file-document-outline';
  return 'mdi:paperclip';
}

const KkpBatValideModal = ({
  show,
  vente,
  onClose,
  onSubmit,
  submitting = false,
  error = null,
}) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const existing = vente?.crea_pub_pieces_jointes ?? [];
  const hasExisting = existing.length > 0;
  const imageOptional = canSkipBatValideImage();
  const canValidate = !submitting && (files.length > 0 || hasExisting || imageOptional);

  useEffect(() => {
    if (show) {
      setFiles([]);
      setDragActive(false);
    }
  }, [show, vente?.id]);

  const handleClose = () => {
    if (submitting) return;
    setFiles([]);
    setDragActive(false);
    onClose?.();
  };

  const addFiles = (fileList) => {
    const picked = Array.from(fileList ?? []);
    if (picked.length === 0) return;
    setFiles((prev) => [...prev, ...picked]);
  };

  const handlePickFiles = (event) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (submitting) return;
    addFiles(event.dataTransfer?.files);
  };

  const handleSubmit = async () => {
    if (!canValidate) return;
    await onSubmit?.(files);
  };

  if (!show || !vente) return null;

  return (
    <AppModal
      show
      size="lg"
      centered
      onClose={handleClose}
      closeDisabled={submitting}
      headerClassName="align-items-center"
      title={(
        <span className="d-flex align-items-center gap-2 flex-wrap">
          <span>Valider le BAT — {vente.client_label}</span>
          {venteCanalLabel(vente) ? (
            <span className="badge kkp-pub-graphiste-canal-badge">{venteCanalLabel(vente)}</span>
          ) : null}
        </span>
      )}
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={handleClose} disabled={submitting}>Annuler</AppButton>
          <AppButton
            variant="success"
            icon="mdi:check"
            loading={submitting}
            loadingLabel="Validation…"
            onClick={handleSubmit}
            disabled={!canValidate}
          >
            Valider le BAT
          </AppButton>
        </>
      )}
    >
      <dl className="row g-2 mb-3 kkp-pub-graphiste-brief-meta">
        <div className="col-sm-6">
          <dt className="text-secondary-light">Format</dt>
          <dd className="mb-0">{vente.format || '—'}</dd>
        </div>
        <div className="col-sm-6">
          <dt className="text-secondary-light">Contenu</dt>
          <dd className="mb-0">{vente.contenu || '—'}</dd>
        </div>
        <div className="col-sm-6">
          <dt className="text-secondary-light">Période</dt>
          <dd className="mb-0">{ventePeriodeLabel(vente)}</dd>
        </div>
      </dl>

      <AppAlert variant="info" className="mb-3">
        {imageOptional
          ? 'En tant que super administrateur, vous pouvez valider le BAT sans joindre de visuel. Ajoutez-en un si disponible.'
          : 'La dernière version de la pub a-t-elle bien été ajoutée ? Vérifiez le visuel ci-dessous, remplacez-le si besoin, puis validez.'}
      </AppAlert>

      {files.length === 0 && hasExisting ? (
        <KkpBriefAttachmentsViewer
          venteId={vente.id}
          piecesJointes={existing}
          fetchBlob={(fileId, opts) => kkpService.fetchCreaPubFichierBlob(vente.id, fileId, opts)}
          downloadFile={(fileId, filename) => kkpService.downloadCreaPubFichier(vente.id, fileId, filename)}
          title="Visuel actuellement attaché"
        />
      ) : null}

      {!hasExisting && files.length === 0 ? (
        <AppAlert variant={imageOptional ? 'info' : 'warning'} className="mb-3">
          {imageOptional
            ? 'Aucun visuel attaché. Vous pouvez tout de même valider le BAT, ou en déposer un ci-dessous.'
            : 'Aucun visuel n’est encore attaché. Déposez la dernière version de la pub pour valider le BAT.'}
        </AppAlert>
      ) : null}

      <div className="kkp-brief-fichiers mt-3">
        <label className="form-label fw-medium mb-2">
          {hasExisting || imageOptional ? 'Remplacer / ajouter un visuel (optionnel)' : 'Dernière version de la pub'}
        </label>
        <div
          className={[
            'kkp-brief-fichiers__dropzone',
            dragActive ? 'is-active' : '',
            files.length > 0 ? 'has-files' : '',
          ].filter(Boolean).join(' ')}
          onDrop={handleDrop}
          onDragOver={(event) => {
            event.preventDefault();
            if (!submitting) setDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragActive(false);
          }}
          onClick={() => !submitting && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (!submitting) inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Zone de dépôt de fichiers"
        >
          <input
            ref={inputRef}
            type="file"
            className="d-none"
            multiple
            accept={BRIEF_ACCEPTED_FILE_TYPES}
            onChange={handlePickFiles}
          />

          {files.length === 0 ? (
            <div className="kkp-brief-fichiers__dropzone-empty">
              <Icon icon="mdi:cloud-upload-outline" className="kkp-brief-fichiers__dropzone-icon" />
              <span className="kkp-brief-fichiers__dropzone-text">
                {hasExisting
                  ? 'Glissez un nouveau fichier pour remplacer le visuel, ou validez sans changement'
                  : imageOptional
                    ? 'Optionnel : glissez-déposez un visuel ici, ou validez directement'
                    : 'Glissez-déposez le visuel ici ou cliquez pour parcourir'}
              </span>
            </div>
          ) : (
            <ul className="kkp-brief-fichiers__list mb-0">
              {files.map((file, index) => (
                <li key={`${file.name}-${file.size}-${index}`} className="kkp-brief-fichiers__item">
                  <Icon icon={fileIconFromName(file.name)} className="kkp-brief-fichiers__icon" width={20} />
                  <div className="kkp-brief-fichiers__meta">
                    <div className="kkp-brief-fichiers__name">{file.name}</div>
                    <div className="kkp-brief-fichiers__size">{formatBriefFileSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={(event) => {
                      event.stopPropagation();
                      setFiles((prev) => prev.filter((_, i) => i !== index));
                    }}
                    disabled={submitting}
                    title="Retirer"
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {error && <AppAlert className="mt-3 mb-0">{error}</AppAlert>}
    </AppModal>
  );
};

export default KkpBatValideModal;
